// ============================================================================
// INTERESTS GLYPHS — transcribed path-for-path from docs/design/handoff/"LifeOS
// Interests.dc.html", option 7a.
//
// Same rules as components/icons.tsx: viewBox 0 0 24 24, fill none, round caps
// and joins, and `stroke="currentColor"` always — the colour comes from a token
// class on the caller, never from a prop, which is what keeps the ship gate's
// brand audit satisfiable.
//
// ONE GLYPH HAS NO MOCKUP SOURCE: `XMark`. The mockup draws no x.com save, and
// the demo corpus has three. It is drawn in the same idiom and should be
// replaced the day a mockup carries one.
// ============================================================================

import type { IconProps } from '../icons'

function Svg({
  size,
  strokeWidth,
  className,
  children,
}: IconProps & { children: React.ReactNode }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={strokeWidth}
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
      aria-hidden="true"
      focusable="false"
    >
      {children}
    </svg>
  )
}

/**
 * The ask bar's mark: one large four-point star and a small one beside it. The
 * same sparkle the Chat screen uses for the assistant, at the one size the
 * mockup draws it — 21px, and the small variant at 12px drops the second star.
 */
export const Sparkle = ({ size = 21, strokeWidth = 1.7, className }: IconProps) => (
  <Svg size={size} strokeWidth={strokeWidth} className={className}>
    <path d="M12 3.4l1.9 4.7 4.7 1.9-4.7 1.9L12 16.6l-1.9-4.7L5.4 10l4.7-1.9zM18.6 15.4l.8 2 2 .8-2 .8-.8 2-.8-2-2-.8 2-.8z" />
  </Svg>
)

/** The single-star sparkle: the `Summarize these` pill and the AI SUMMARY label. */
export const SparkleSmall = ({ size = 12, strokeWidth = 1.9, className }: IconProps) => (
  <Svg size={size} strokeWidth={strokeWidth} className={className}>
    <path d="M12 3.4l1.9 4.7 4.7 1.9-4.7 1.9L12 16.6l-1.9-4.7L5.4 10l4.7-1.9z" />
  </Svg>
)

/** `Ask →`, and the arrow on the "4 more in AI video models" row. */
export const ArrowRight = ({ size = 13, strokeWidth = 2.2, className }: IconProps) => (
  <Svg size={size} strokeWidth={strokeWidth} className={className}>
    <path d="M5 12h13M12.6 6l6 6-6 6" />
  </Svg>
)

/** `Open original ↗`. */
export const ArrowOut = ({ size = 12, strokeWidth = 2, className }: IconProps) => (
  <Svg size={size} strokeWidth={strokeWidth} className={className}>
    <path d="M9 5h10v10M19 5 7 17" />
  </Svg>
)

// ── Source glyphs ───────────────────────────────────────────────────────────
// One per SaveSource. They ride in a 20px tile whose ground is the source's own
// tint; the tile is what makes a wall of cards scannable by where a thing came
// from before a single title is read.

/** Reddit. */
export const RedditFace = ({ size = 12, strokeWidth = 1.9, className }: IconProps) => (
  <Svg size={size} strokeWidth={strokeWidth} className={className}>
    <path d="M12 4.2a7.8 7.8 0 1 1 0 15.6 7.8 7.8 0 0 1 0-15.6M8.6 11.4a1 1 0 1 0 0 .01M15.4 11.4a1 1 0 1 0 0 .01M9 15.2c1.8 1.2 4.2 1.2 6 0" />
  </Svg>
)

/** YouTube. */
export const VideoBox = ({ size = 12, strokeWidth = 1.9, className }: IconProps) => (
  <Svg size={size} strokeWidth={strokeWidth} className={className}>
    <path d="M3.4 8.4A2.4 2.4 0 0 1 5.8 6h12.4a2.4 2.4 0 0 1 2.4 2.4v7.2a2.4 2.4 0 0 1-2.4 2.4H5.8a2.4 2.4 0 0 1-2.4-2.4zM10.4 9.8 14.6 12l-4.2 2.2z" />
  </Svg>
)

/** An article on the open web — a page with a folded corner. */
export const Document = ({ size = 12, strokeWidth = 1.9, className }: IconProps) => (
  <Svg size={size} strokeWidth={strokeWidth} className={className}>
    <path d="M6 4.6h8.4L18.4 8.6v10.8H6zM9 12h6M9 15.4h4" />
  </Svg>
)

/** X. NO MOCKUP SOURCE — see the file header. */
export const XMark = ({ size = 12, strokeWidth = 1.9, className }: IconProps) => (
  <Svg size={size} strokeWidth={strokeWidth} className={className}>
    <path d="M6 6l12 12M18 6 6 18" />
  </Svg>
)

/**
 * The media placeholder on a video save. There are no bitmaps in this bundle
 * (README §Assets), so a video card gets a gradient band with this play-in-a-
 * frame glyph centred in it — which is what the mockup draws, not a stand-in
 * for a thumbnail that never arrives.
 */
export const PlayFrame = ({ size = 26, strokeWidth = 1.6, className }: IconProps) => (
  <Svg size={size} strokeWidth={strokeWidth} className={className}>
    <path d="M9.8 8.6 16 12l-6.2 3.4z" />
    <path d="M3.4 7.4A2.4 2.4 0 0 1 5.8 5h12.4a2.4 2.4 0 0 1 2.4 2.4v9.2a2.4 2.4 0 0 1-2.4 2.4H5.8a2.4 2.4 0 0 1-2.4-2.4z" />
  </Svg>
)
